// Scroll chaining for the embedded viewer.
//
// A cross-origin iframe swallows wheel/touch events, so the host page can never
// see that the viewer has hit its own scroll boundary. This runs inside the
// iframe: once nothing here can absorb the gesture any more, the leftover delta
// is posted up to embed.js, which scrolls the host page by the same amount.

const MESSAGE_TYPE = "ecollat:scroll-chain";

const isScrollable = (element) => {
  if (!(element instanceof Element)) return false;
  const overflowY = window.getComputedStyle(element).overflowY;
  if (overflowY !== "auto" && overflowY !== "scroll" && overflowY !== "overlay") {
    return false;
  }
  return element.scrollHeight - element.clientHeight > 1;
};

// Can `element` still move in the direction of `deltaY` (positive = downwards)?
const canAbsorb = (element, deltaY) => {
  const max = element.scrollHeight - element.clientHeight;
  return deltaY > 0 ? element.scrollTop < max - 1 : element.scrollTop > 1;
};

// Walk the ancestor chain the browser would scroll, ending at the document.
const chainAbsorbs = (target, deltaY) => {
  let node = target;
  while (node && node !== document && node !== document.documentElement) {
    if (isScrollable(node) && canAbsorb(node, deltaY)) return true;
    node = node.parentElement;
  }
  const scroller = document.scrollingElement || document.documentElement;
  return scroller.scrollHeight - scroller.clientHeight > 1 && canAbsorb(scroller, deltaY);
};

const sendToHost = (deltaY) => {
  if (!deltaY) return;
  window.parent.postMessage({ type: MESSAGE_TYPE, deltaY }, "*");
};

export function setupEmbedScrollChain() {
  if (window.parent === window) return;
  if (window.__ecollatScrollChain) return;
  window.__ecollatScrollChain = true;

  window.addEventListener(
    "wheel",
    (event) => {
      if (event.deltaY && !chainAbsorbs(event.target, event.deltaY)) {
        sendToHost(event.deltaY);
      }
    },
    { passive: true }
  );

  let lastY = null;

  window.addEventListener(
    "touchstart",
    (event) => {
      lastY = event.touches.length === 1 ? event.touches[0].clientY : null;
    },
    { passive: true }
  );

  window.addEventListener(
    "touchmove",
    (event) => {
      if (lastY === null || event.touches.length !== 1) return;
      const currentY = event.touches[0].clientY;
      // Dragging the finger up (currentY shrinking) scrolls the content down.
      const deltaY = lastY - currentY;
      lastY = currentY;
      if (deltaY && !chainAbsorbs(event.target, deltaY)) {
        sendToHost(deltaY);
      }
    },
    { passive: true }
  );

  window.addEventListener(
    "touchend",
    () => {
      lastY = null;
    },
    { passive: true }
  );
}
